// TypeScript types for the workflow config shape — mirrors C# DTOs
// VoiceSpectrum.Web.T2 · v10.0.8.5 · 2026-05-17 · VoiceSpectrum AI

// ── Pre-routing ────────────────────────────────────────────────────────────

export type PreRoutingRole =
  | 'start'
  | 'disclosure'
  | 'hours'
  | 'greeting'
  | 'lang'
  | 'classify';

export interface DisclosureSubSection {
  enabled: boolean;
  text: Record<string, string>;
}

export interface WeeklySchedule {
  [day: string]: { open: string; close: string; closed: boolean };
}

export interface PreRoutingFields {
  channels?: string[];
  timeout?: number;
  master_enabled?: boolean;
  recording?: DisclosureSubSection;
  legal?: DisclosureSubSection;
  gap_ms?: number;
  timezone?: string;
  schedule?: WeeklySchedule;
  greeting_text?: Record<string, string>;
  voice_override?: Record<string, string>;
  detect_lang_prompt?: Record<string, string>;
  classification_prompt?: Record<string, string>;
}

export interface PreRoutingNode {
  id: string;
  role: PreRoutingRole;
  name: string;
  icon: string;
  enabled: boolean;
  fields: PreRoutingFields;
  pack_default: PreRoutingFields;
}

// ── Workflow / Intent / Node ──────────────────────────────────────────────

// v12.7.3 (#648): 'Request' is the default and 'Steps' is retired - but it stays in the union because an
// existing snapshot may still carry one and must keep loading (AC 648-d).
export type NodeType = 'Identity' | 'Request' | 'Steps' | 'Connect' | 'Confirm';

export interface IdentityField {
  field_key: string;
  label: string;
  prompt: Record<string, string>;
  required: boolean;
}

export interface StepOnResponse {
  on_conflict?: StepAction;
  on_error?: StepAction;
  on_timeout?: StepAction;
}

export type StepOnFail = 'reask' | 'capture-as-said' | 'skip' | 'escalate';

export interface Step {
  id: string;
  label: string;
  prompt: Record<string, string>;
  on_fail: StepOnFail;
  max_retries: number;
  on_response: StepOnResponse;
}

export type StepAction = 'continue' | 'retry' | 'escalate' | 'skip';

export interface ConnectConfig {
  integration_ref: string;
  operation: string;
  payload_mapping: Record<string, string>;
}

export interface ConnectOutput {
  key: string;
  label: string;
}

export type ClassificationPreset =
  | 'very-strict'
  | 'strict'
  | 'lenient'
  | 'very-lenient'
  | 'custom';

export const CLASSIFICATION_THRESHOLD: Record<ClassificationPreset, number | null> = {
  'very-strict':  0.90,
  'strict':       0.75,
  'lenient':      0.60,
  'very-lenient': 0.45,
  'custom':       null,
};

export interface ClassificationConfig {
  preset: ClassificationPreset;
  custom_threshold?: number;
}

export interface WorkflowNode {
  id: string;
  name: string;
  type: NodeType;
  enabled: boolean;
  silent?: boolean;                     // for Connect nodes: webhook-only = suppress lang tabs
  retry_override?: number;
  identity: IdentityField[];
  steps: Step[];
  connect?: ConnectConfig;
  outputs: ConnectOutput[];
  classification?: ClassificationConfig;
  skip_confirm_acknowledgement?: Record<string, string>;  // D-61: Confirm node only
  // For diff / Modified pill
  pack_default?: Partial<WorkflowNode>;
  sections?: CardSection[];             // D-59: derived structure for lang-tab suppression
}

// D-59: section structure for shouldSuppressLangTabs
export interface CardField {
  perLanguage: boolean;
  [key: string]: unknown;
}

export interface CardSection {
  fields?: CardField[];
}

export interface Intent {
  id: string;
  name: string;
  nodes: WorkflowNode[];
  classification: ClassificationConfig;
}

export interface Workflow {
  id: string;
  name: string;
  description?: string;
  status?: string;
  intents: Intent[];
  pre_routing?: PreRoutingNode[];
}

// ── Terminals ─────────────────────────────────────────────────────────────

export type HandoffMode = 'Live transfer' | 'Voicemail' | 'Both';

export interface Terminal {
  handoff: {
    mode: HandoffMode;
    handoff_text: Record<string, string>;
  };
  voicemail: {
    voicemail_text: Record<string, string>;
    max_seconds: number;
  };
  endcall: {
    endcall_text: Record<string, string>;
  };
}

// ── Token registry ────────────────────────────────────────────────────────

export type TokenScope = 'tenant' | 'call' | 'connect_output';

export interface TokenEntry {
  key: string;
  scope: TokenScope;
  label: string;
  source_node?: string;
}

export const BASE_TOKEN_REGISTRY: TokenEntry[] = [
  { key: 'business_name',       scope: 'tenant', label: 'Business Name' },
  { key: 'business_hours',      scope: 'tenant', label: 'Business Hours' },
  { key: 'persona_voice',       scope: 'tenant', label: 'Persona Voice' },
  { key: 'supported_languages', scope: 'tenant', label: 'Supported Languages' },
  { key: 'timezone',            scope: 'tenant', label: 'Timezone' },
  { key: 'caller_first_name',   scope: 'call',   label: 'Caller First Name' },
  { key: 'caller_full_name',    scope: 'call',   label: 'Caller Full Name' },
  { key: 'caller.name',         scope: 'call',   label: 'Caller Name' },
  { key: 'caller_phone',        scope: 'call',   label: 'Caller Phone' },
  { key: 'dob',                 scope: 'call',   label: 'Date of Birth' },
  { key: 'chart_number',        scope: 'call',   label: 'Chart Number' },
  { key: 'member_id',           scope: 'call',   label: 'Member ID' },
  { key: 'auth_id',             scope: 'call',   label: 'Auth ID' },
  { key: 'slot.reason',         scope: 'call',   label: 'Slot Reason' },
];

export const TOKEN_PATTERN = /\{([a-zA-Z0-9_.]+)\}/g;

export function validateTokens(
  text: string,
  availableTokens: TokenEntry[],
): { valid: string[]; unknown: string[] } {
  const keys = availableTokens.map(t => t.key);
  const found = [...text.matchAll(TOKEN_PATTERN)].map(m => m[1]);
  return {
    valid:   found.filter(k => keys.includes(k)),
    unknown: found.filter(k => !keys.includes(k)),
  };
}

export const CONFIRM_SKIP_ACK_DEFAULT: Record<string, string> = { en: '', es: '' };

// Confirm node pack default for skip_confirm_acknowledgement
export function getSkipConfirmAck(node: WorkflowNode, lang: string): string {
  return node.skip_confirm_acknowledgement?.[lang] ?? '';
}
